import styled from '@emotion/styled';
import { useMemo } from 'react';
import { ChevronLeft, Gift, Truck, Tag, Star, ShieldCheck } from 'lucide-react';
import type { CustomerSubscription, Perk } from '@/domain/entities/subscription.entity';

const GENERIC_BENEFITS = [
  { name: 'Free Shipping', description: 'On all orders', icon: Truck },
  { name: 'Exclusive Discounts', description: 'Members-only pricing', icon: Tag },
  { name: 'Priority Support', description: 'Skip the queue', icon: Star },
  { name: 'Early Access', description: 'New products first', icon: ShieldCheck },
];

function perkIcon(perk: Perk) {
  switch (perk.type) {
    case 'shipping':
      return Truck;
    case 'product':
      return Tag;
    case 'service':
      return Star;
    default:
      return Star;
  }
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'N/A';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

// -- Styled Components --

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-bottom: 48px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #292929;
  display: flex;
  align-items: center;

  &:hover {
    opacity: 0.7;
  }
`;

const HeaderTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  line-height: 1.4;
  color: #292929;
  margin: 0;
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SubscriptionCard = styled.div`
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const PlanName = styled.h3`
  font-size: 20px;
  font-weight: 600;
  line-height: 1.4;
  color: #292929;
  margin: 0;
`;

const CardDivider = styled.hr`
  border: none;
  border-top: 1px solid #e5e5e5;
  margin: 0;
`;

const RetentionHighlight = styled.div`
  background: #fdf1ef;
  border: 1px solid rgba(136, 42, 43, 0.5);
  border-radius: 8px;
  padding: 14px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const GiftIconCircle = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #882a2b;
`;

const OfferTextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const OfferTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  line-height: 1.43;
  color: #292929;
`;

const OfferDescription = styled.span`
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  color: #5a5a5a;
`;

const DetailsHeading = styled.h4`
  font-size: 20px;
  font-weight: 600;
  line-height: 1.4;
  color: #292929;
  margin: 0;
`;

const DetailsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const DetailRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const DetailLabel = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 1.57;
  color: #5a5a5a;
`;

const DetailValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  line-height: 1.57;
  color: #292929;
`;

const BenefitsCard = styled.div`
  background: #f7f7f7;
  border-radius: 16px;
  padding: 16px 12px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const BenefitsHeading = styled.h4`
  font-size: 18px;
  font-weight: 600;
  line-height: 1.33;
  color: #292929;
  margin: 0;
  text-align: center;
  max-width: 280px;
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 11px;
  width: 100%;
`;

const BenefitItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  border-radius: 8px;
`;

const BenefitIconCircle = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #fcd9d1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #292929;
`;

const BenefitTextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: center;
`;

const BenefitName = styled.span`
  font-size: 14px;
  font-weight: 600;
  line-height: 1.43;
  color: #292929;
`;

const BenefitDescription = styled.span`
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  color: #5a5a5a;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #d1d1d1;
  margin: 0;
  width: 100%;
`;

const CTASection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PrimaryButton = styled.button`
  width: 100%;
  padding: 14px 32px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
  background: #f7a08b;
  border: none;
  color: #292929;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.85;
  }
`;

const SecondaryButton = styled.button`
  width: 100%;
  padding: 14px 32px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
  background: #292929;
  border: none;
  color: #f6f0e7;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.85;
  }
`;

// -- Component --

interface RetentionOfferPageProps {
  readonly subscription: CustomerSubscription;
  readonly onAcceptOffer: () => void;
  readonly onDeclineOffer: () => void;
  readonly onBack: () => void;
}

export function RetentionOfferPage({
  subscription,
  onAcceptOffer,
  onDeclineOffer,
  onBack,
}: RetentionOfferPageProps) {
  const isMembership = subscription.plan.type === 'membership';
  const pageTitle = isMembership ? 'Cancel Membership' : 'Cancel Subscription';

  const matchedFrequency = subscription.plan.frequencies?.find(
    (f) => f.id === subscription.plan_frequency_id,
  );

  const priceLabel = useMemo(() => {
    if (!matchedFrequency) return 'N/A';
    return `${formatPrice(matchedFrequency.price_display)} USD/month`;
  }, [matchedFrequency]);

  const activePerks = subscription.plan.perks?.filter((p) => p.is_active) ?? [];
  const hasBenefits = activePerks.length > 0;

  const benefits = useMemo(() => {
    if (hasBenefits) {
      return activePerks.map((perk) => ({
        name: perk.name,
        description: perk.description,
        Icon: perkIcon(perk),
      }));
    }
    return GENERIC_BENEFITS.map((b) => ({
      name: b.name,
      description: b.description,
      Icon: b.icon,
    }));
  }, [activePerks, hasBenefits]);

  return (
    <PageContainer data-testid="retention-offer-page">
      <Header>
        <BackButton onClick={onBack} aria-label="Back">
          <ChevronLeft size={22} />
        </BackButton>
        <HeaderTitle>{pageTitle}</HeaderTitle>
      </Header>

      <ContentSection>
        <SubscriptionCard>
          <PlanName>{subscription.plan.name}</PlanName>

          <CardDivider />

          <RetentionHighlight data-testid="retention-highlight">
            <GiftIconCircle>
              <Gift size={20} />
            </GiftIconCircle>
            <OfferTextBlock>
              <OfferTitle>Free Generic Offer</OfferTitle>
              <OfferDescription>
                Stay and get 2 free months on us — no charges for the next 2
                billing cycles.
              </OfferDescription>
            </OfferTextBlock>
          </RetentionHighlight>

          <DetailsHeading>Subscription Details</DetailsHeading>

          <DetailsList>
            <DetailRow>
              <DetailLabel>Next billing date</DetailLabel>
              <DetailValue>{formatDate(subscription.next_charge_date)}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>Price</DetailLabel>
              <DetailValue>{priceLabel}</DetailValue>
            </DetailRow>
          </DetailsList>
        </SubscriptionCard>

        <BenefitsCard data-testid="benefits-section">
          <BenefitsHeading>
            You&rsquo;ll lose access to these exclusive benefits.
          </BenefitsHeading>
          <BenefitsGrid>
            {benefits.map((benefit) => (
              <BenefitItem key={benefit.name}>
                <BenefitIconCircle>
                  <benefit.Icon size={22} />
                </BenefitIconCircle>
                <BenefitTextBlock>
                  <BenefitName>{benefit.name}</BenefitName>
                  <BenefitDescription>{benefit.description}</BenefitDescription>
                </BenefitTextBlock>
              </BenefitItem>
            ))}
          </BenefitsGrid>
        </BenefitsCard>
      </ContentSection>

      <Divider />

      <CTASection>
        <PrimaryButton
          type="button"
          onClick={onAcceptOffer}
          data-testid="accept-offer-btn"
        >
          Stay with my {isMembership ? 'membership' : 'subscription'} &amp; get 2 free months
        </PrimaryButton>
        <SecondaryButton
          type="button"
          onClick={onDeclineOffer}
          data-testid="decline-offer-btn"
        >
          finish cancellation
        </SecondaryButton>
      </CTASection>
    </PageContainer>
  );
}
